import React, { Component } from 'react';
import { scaleTime } from '@vx/scale';
import { AxisLeft } from '@vx/axis';
import { GridRows } from '@vx/grid';
import { Dialog, DialogTitle, DialogActions, Button, IconButton, Typography, } from '@material-ui/core';
import { withStyles } from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import { DatePicker } from 'material-ui-pickers';
import PropTypes from 'prop-types';
import GameCard from './cards/GameCard';
import format from 'date-fns/format';
import moment from 'moment-timezone';
import './DailySummary.css';

const styles = theme => ({
  button: {
    margin: 0,
    padding: 0,
    fontSize: 14,
    width: 40,
  },
});

class DailySummary extends Component {
  state = {
    isOpen: false,
    selectedGame: null,
    isSelectedGameCoach: false,
    summaryDate: '',
    minScheduleDate: '',
    maxScheduleDate: ''
  };

  componentDidMount() {
    const { fetchingDailySummary, getDailySummary, getUmpInfo, umpiresList,
      memberId, userSeason, getCoachesTeams, getSeasonDivisions } = this.props;
    
    if (fetchingDailySummary !== 'Loading' || fetchingDailySummary !== 'Loaded') {
      const startDate = format(new Date(), 'YYYY-MM-DD');
      getDailySummary(startDate);
      this.setState({ summaryDate: startDate });
    }
    if (umpiresList.length === 0) {
      getUmpInfo();
    }
    if (userSeason) {
      getCoachesTeams(memberId, userSeason);
      getSeasonDivisions(userSeason);
      this.setScheduleRange();
    }
  }

  componentDidUpdate(prevProps) {
    const { memberId, userSeason, games, 
      getDailySummary, getCoachesTeams, getSeasonDivisions } = this.props;
    
      //Retrieve Daily Summary if Season changes or Game information changes
    if ((userSeason !== prevProps.userSeason) ||
        (games !== prevProps.games)) {
      getDailySummary(this.state.summaryDate);
      getCoachesTeams(memberId, userSeason);
      getSeasonDivisions(userSeason);
      this.setScheduleRange();
    }
  }

  setScheduleRange = () =>{
    const { userSeason, seasons } = this.props;
    const isAdmin = this.isAdmin();
    const today = moment().format();
    const tomorrow = moment().add(1, 'days').format();
    let minScheduleDate, maxScheduleDate;

    //Set the date range of the filter based on the role
    if (userSeason) {
      if (isAdmin) {
        //min is earliest of today and season start.
        //max is the latest of tomorrow and season end
        minScheduleDate = (today < seasons[userSeason].start) ? today : seasons[userSeason].start;
        maxScheduleDate = (tomorrow > seasons[userSeason].finish) ? tomorrow : seasons[userSeason].finish;
      } else {
        //min is today
        //max is the max schedule date of the season.
        minScheduleDate = today;
        maxScheduleDate = seasons[userSeason].maxScheduleDate;
      }
    }
    this.setState({ minScheduleDate: moment(minScheduleDate).format('YYYY-MM-DD') });
    this.setState({ maxScheduleDate: moment(maxScheduleDate).format('YYYY-MM-DD') });
  }

  toggleDialog = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleOnClick = e => {
    const { id } = e.target;
    const { games } = this.props.dailySummary;
    const game = games[id];
    const { teamsCoaching } = this.props;
    let isGameCoach = false;

    //Check to see if the User is the Coach for the selected Game
    teamsCoaching.forEach((team) => {
      if (game.homeTeamId === team.teamId || game.awayTeamId === team.teamId) isGameCoach = true;
    });

    //Update the selected Game information in the state
    this.setState({ selectedGame: id });
    this.setState({ isSelectedGameCoach: isGameCoach });
    this.toggleDialog();
  };

  handleDateChange = selectedDate => {
    const newDate = selectedDate.format('YYYY-MM-DD');

    this.resetDailySummary(newDate);
  };

  handleAddDay = value => {
    const { summaryDate } = this.state;
    const { minScheduleDate, maxScheduleDate } = this.state;
    const newDate = moment(summaryDate).add(value, 'days').format('YYYY-MM-DD');

    //Set the new Date if it is within the allowed schedule range
    if (newDate >= minScheduleDate && newDate <= maxScheduleDate)
      this.resetDailySummary(newDate);
  };

  resetDailySummary = newDate => {
    const { getDailySummary } = this.props;
    if (newDate !== this.state.summaryDate ) {
      getDailySummary(newDate);
      this.setState({ summaryDate: newDate });
      this.setState({ selectedGame: null });
      this.setState({ isSelectedGameCoach: false });
      this.setState({ isOpen: false });
    }
  }

  isAdmin = () => {
    return this.props.userType === 'Admin';
  }

  isUmpire = () => {
    return this.props.umpireLevel !== null && this.props.umpireLevel > 0;
  }

  allLoaded = () => {
    //Check that all required state objects have been loaded
    const {
      fetchingDailySummary, fetchingSeasonDivisionsStatus,
      fetchingUmpiresStatus, fetchingCoachTeamsStatus
    } = this.props;
    return  fetchingDailySummary === 'Loaded' && fetchingCoachTeamsStatus === 'Loaded' &&
            fetchingSeasonDivisionsStatus === 'Loaded' && fetchingUmpiresStatus === 'Loaded';
  }

  render() {
    let { fields, fieldList = [], minDate, maxDate, games } = this.props.dailySummary;
    const { selectedGame, isSelectedGameCoach, minScheduleDate, maxScheduleDate } = this.state;
    const { classes } = this.props;

    const facilityNames = fieldList.map(field => field.facilityName);
    const isUmpire = this.isUmpire();
    const isAdmin = this.isAdmin();

    let svgRect = [];
    minDate = moment.unix(minDate);
    maxDate = moment.unix(maxDate);

    const timeRange = moment.duration(maxDate - minDate);
    const svgHeight = timeRange <= 32400000 ? 900 : 1250;
    const timeScale = scaleTime({
      range: [0, svgHeight],
      domain: [minDate, maxDate]
    });

    const yellow = '#fac752';
    const red = '#df5353';
    const blue = '#5db7ef';
    const grey = '#cccccc';

    fieldList.forEach((field, index) => {
      let result = fields[field.facilityId].gameIds.map((gameId, i) => {
        const { epochStart, epochEnd, umpAvailable, shortDivisionName  } = games[gameId];
        const game = games[gameId];
        const top = 0;
        const left = 5;
        const width = 125;
        const xPoint = width * index + left;
        const yPoint = timeScale(moment.unix(epochStart)) + top;
        const rectHeight = timeScale(moment.unix(epochEnd)) - timeScale(moment.unix(epochStart));
        const umpPlate =
          game.umpPlateName === null || game.umpPlateName === ' .' ? '' : game.umpPlateName;
        const base1Ump =
          game.umpBase1Name === null || game.umpBase1Name === ' .' ? '' : game.umpBase1Name;
        const base2Ump =
          game.umpBase2Name === null || game.umpBase2Name === ' .' ? '' : game.umpBase2Name;
        const base3Ump =
          game.umpBase3Name === null || game.umpBase3Name === ' .' ? '' : game.umpBase3Name;

        const plateHasYouth = game.umpPlateIsYouth === true;
        const base1HasYouth = game.umpBase1IsYouth === true;
        const base2HasYouth = game.umpBase2IsYouth === true;
        const base3HasYouth = game.umpBase3IsYouth === true;

        let numUmp = 0;
        if (game.umpPlateName !== null && game.umpPlateName !== ' .') numUmp++;
        if (game.umpBase1Name !== null && game.umpBase1Name !== ' .') numUmp++;
        if (game.umpBase2Name !== null && game.umpBase2Name !== ' .') numUmp++;
        if (game.umpBase3Name !== null && game.umpBase3Name !== ' .') numUmp++;

        let { homeTeamName, awayTeamName } = games[gameId];
        //If no Away Team, set name to "TBD"
        if (!awayTeamName) awayTeamName = "TBD";
        //If Team Name is longer that 22 chars, reduce to fit
        if (homeTeamName && homeTeamName.length > 22) homeTeamName = homeTeamName.substr(0,20) + "...";
        if (awayTeamName && awayTeamName.length > 22) awayTeamName = awayTeamName.substr(0,20) + "...";

        //Set the color of the Game block based on the status and the number of Umpires assigned
        let color;
        if (game.cancelled) {
          color = grey;
        } else {
          //If all assignments are filled, set to blue
          if (numUmp >= game.umpAvailable) {
            color = blue;
          } else if (numUmp > 0 ) {
            //If at least one is filled, set to yellow
            color = yellow;
          } else {
            //If none are filled, set to red
            color = red;
          }
        }

        return (
          <g key={`${gameId}`}>
            <rect
              id={`${gameId}`}
              x={`${xPoint}`}
              y={`${yPoint}`}
              width={`${width}`}
              height={`${rectHeight}`}
              fill={`${color}`}
              stroke="#fff"
              strokeWidth="2"
              onClick={this.handleOnClick}
            />
            <text x={`${xPoint + 5}`} y={`${yPoint + 20}`} fill="#000" fontSize={12}>
              {`${shortDivisionName}`}
            </text>
            <text x={`${xPoint + 5}`} y={`${yPoint + 40}`} fill="#000" fontSize={10}>
              {`H: ${homeTeamName}`}
            </text>
            <text x={`${xPoint + 5}`} y={`${yPoint + 60}`} fill="#000" fontSize={10}>
              {`V: ${awayTeamName}`}
            </text>
            <text x={`${xPoint + 5}`} y={`${yPoint + 80}`} fill="#000" fontSize={12}>
              {`P: ${umpPlate}`}
            </text>
            {plateHasYouth && (
            <text x={xPoint + 15} y={yPoint + 95} fill="#000" fontSize={10}>
              Youth
            </text>
            )}

            {/* Base 1 Name */}
            {umpAvailable >= 2 ? (
              <text x={`${xPoint + 5}`} y={`${yPoint + 100}`} fill="#000" fontSize={12}>
                {`B1: ${base1Ump}${base1HasYouth ? ' Y' : ''}`}
              </text>
            ) : (
              ''
            )}

            {umpAvailable >= 3 ? (
              <text x={`${xPoint + 5}`} y={`${yPoint + 120}`} fill="#000" fontSize={12}>
              {`B2: ${base2Ump}${base2HasYouth ? ' Y' : ''}`}</text>
            ) : (
              ''
            )}
            {umpAvailable >= 4 ? (
              <text x={`${xPoint + 5}`} y={`${yPoint + 140}`} fill="#000" fontSize={12}>
              {`B3: ${base3Ump}${base3HasYouth ? ' Y' : ''}`}</text>
            ) : (
              ''
            )}
            }
          </g>
        );
      });
      svgRect = [...svgRect, ...result];
    });

    return (
      <div className="daily-summary-container">
        <div className="daily-summary-header">
          <Typography variant="h5">
            Daily Summary
          </Typography>
          <IconButton
              size="small" color="primary"
              onClick={() => this.handleAddDay(-1)}
          >
            <ArrowBackIcon />
          </IconButton>
          <DatePicker
            value={this.state.summaryDate}
            onChange={this.handleDateChange}
            autoOk
            showTodayButton
            minDate={minScheduleDate}
            maxDate={maxScheduleDate}
            format="ddd, MMM Do YYYY"
          />
          <IconButton
              size="small" color="primary"
              onClick={() => this.handleAddDay(1)}
          >
            <ArrowForwardIcon />
          </IconButton>
        </div>
        <div className="daily-summary-chart-container">
          <svg className="daily-summary-chart" width={1825} height={svgHeight+100}>
            <GridRows
              top={30}
              left={70}
              scale={timeScale}
              width={1825}
              strokeDasharray="2,2"
              stroke="rgb(87, 94, 102)"
            />
            <g className="daily-summary-group" transform="translate(70, 30)">
              {svgRect}
            </g>
            <g className="daily-summary-top-axis-group" transform="translate(70, 30)">
              {facilityNames.map((facility, index) => {
                return (
                  <g key={`${facility}-${index}`} className="top-axis-tick" transform="translate(0, 0)">
                    <line
                      x1={`${125 * index + 70}`}
                      y1="0"
                      x2={`${125 * index + 70}`}
                      y2="-8"
                      fill={'rgb(87, 94, 102)'}
                      stroke={'rgb(87, 94, 102)'}
                      strokeWidth="1"
                    />
                    <text
                      x={`${125 * index + 70}`}
                      y="-8"
                      dy="-0.25em"
                      fill={'rgb(87, 94, 102)'}
                      textAnchor={'middle'}
                      fontSize={14}
                      fontFamily="lato"
                      fontWeight="bold"
                    >
                      {facility}
                    </text>
                  </g>
                );
              })}
              <line
                className="vx-line vx-axis-line"
                x1="0.5"
                y1="0"
                x2="1825"
                y2="0"
                fill={'rgb(87, 94, 102)'}
                stroke="rgb(87, 94, 102)"
                strokeWidth="1.25"
              />
            </g>

            <AxisLeft
              top={30}
              left={70}
              scale={timeScale}
              stroke={'rgb(87, 94, 102)'}
              strokeWidth={1.25}
              numTicks={6}
              tickFormat={tick => format(tick, 'h:mm a')}
              tickComponent={({ formattedValue, ...tickProps }) => (
                <text
                  {...tickProps}
                  fill={'rgb(87, 94, 102)'}
                  textAnchor={'middle'}
                  fontSize={14}
                  fontFamily="lato"
                  fontWeight="bold"
                  dx="-2em"
                >
                  {formattedValue}
                </text>
              )}
            />
          </svg>
        </div>
        <Dialog
          open={this.state.isOpen}
          onClose={this.toggleDialog}
          maxWidth="md"
          fullWidth={true}
        >
          <DialogTitle>Game Details</DialogTitle>
          <div className="pt-dialog-body">
            {selectedGame ? (
              <GameCard
                games={games}
                gameId={selectedGame}
                umpires={this.props.umpires}
                umpiresList={this.props.umpiresList}
                memberId={this.props.memberId}
                isUmpire={isUmpire}
                isAdmin={isAdmin}
                isCoach={isSelectedGameCoach}
                assignUmpToGame={this.props.assignUmpToGame}
                removeUmp={this.props.removeUmp}
                updateUmpAvailableForGame={this.props.updateUmpAvailableForGame}
                allowsAssigning={true}
                seasonDivisions={this.props.seasonDivisions}
                isRow={true}
              />
            ) : (
              ''
            )}
          </div>
          <DialogActions>
            <Button className={classes.button}
              variant="contained" size="small" color="primary"
              onClick={this.toggleDialog}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

DailySummary.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

export default withStyles(styles, { withTheme: true })(DailySummary);



// WEBPACK FOOTER //
// ./src/components/DailySummaryV2.js